// src/pages/Exam/OptionCard.jsx
import React, { useMemo, useRef } from "react";
import { useExam } from "../../contexts/ExamContext";

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const OptionCard = ({ questionId, option, optionIndex = 0, showLetters = false, showRadio = false, interactive = true }) => {
    const {
        answers,
        setAnswer,
        disabledOptions,
        toggleOptionDisabled,
        optionHtmlOverrides,
        setOptionHtml,
    } = useExam();

    const selected = answers?.[questionId] === option.id;
    const letter = letters[optionIndex] ?? "?";
    const disabled = !!disabledOptions?.[questionId]?.[option.id];

    const optRef = useRef(null);
    const contentHtml = useMemo(() => {
        const byQ = optionHtmlOverrides?.[questionId] || {};
        return byQ?.[option.id] ?? option?.answer;
    }, [optionHtmlOverrides, questionId, option]);

    const handleClick = () => {
        if (!interactive) return;
        if (disabled) return;
        // toggle select / deselect
        setAnswer(questionId, selected ? null : option.id);
    };

    const isUserMark = (n) =>
        n && n.nodeType === 1 && n.tagName === "MARK" && n.classList.contains("user-highlight");

    const unwrapMark = (el) => {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
        return parent;
    };

    const removeContiguousGroup = (markEl) => {
        const group = [markEl];
        let n = markEl.previousSibling;
        while (isUserMark(n)) {
            group.unshift(n);
            n = n.previousSibling;
        }
        n = markEl.nextSibling;
        while (isUserMark(n)) {
            group.push(n);
            n = n.nextSibling;
        }
        group.forEach(unwrapMark);
    };

    const handleContextMenu = (e) => {
        if (!interactive) return;
        const container = optRef.current;
        if (container) {
            let el = e.target;
            while (el && el !== container && !isUserMark(el)) el = el.parentNode;
            if (isUserMark(el)) {
                e.preventDefault();
                removeContiguousGroup(el);
                setOptionHtml(questionId, option.id, container.innerHTML);
                return;
            }
        }
        e.preventDefault();
        toggleOptionDisabled(questionId, option.id);
    };

    const applyHighlight = () => {
        if (!interactive) return;
        const container = optRef.current;
        const sel = window.getSelection?.();
        if (!sel || sel.isCollapsed || !container) return;
        if (!container.contains(sel.anchorNode) || !container.contains(sel.focusNode)) return;

        const range = sel.getRangeAt(0);
        try {
            const mark = document.createElement("mark");
            mark.className = "user-highlight";
            range.surroundContents(mark);
            sel.removeAllRanges();
        } catch {
            const frag = range.cloneContents();
            const mark = document.createElement("mark");
            mark.className = "user-highlight";
            mark.appendChild(frag);
            range.deleteContents();
            range.insertNode(mark);
            sel.removeAllRanges();
        }
        setOptionHtml(questionId, option.id, container.innerHTML);
    };

    return (
        <div
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            onMouseUp={applyHighlight}
            role="button"
            aria-disabled={disabled || !interactive}
            className={[
                "p-2 flex align-items-center gap-3 transition-all",
                (!interactive || disabled) ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-blue-300",
                "surface-0",
            ].join(" ")}
        >
            {/* Left badge: MultipleChoice letters OR TrueFalse radio-style circle */}
            {showLetters && (
                <span
                    className={[
                        "flex align-items-center justify-content-center",
                        "w-2rem h-2rem border-1 border-circle",
                        selected ? "bg-primary text-white" : "surface-0 text-900",
                    ].join(" ")}
                    aria-hidden="true"
                >
                    {letter}
                </span>
            )}
            {!showLetters && showRadio && (
                <i
                    className={`pi ${selected ? "pi-circle-fill" : "pi-circle"}`}
                    style={{ fontSize: "1.25rem", color: selected ? "var(--primary-color)" : "#6b7280" }}
                    aria-hidden="true"
                />
            )}

            <style>{`.user-highlight { background: #fde68a; color: #000; padding: 0 .05rem; }`}</style>

            <div ref={optRef} className="exam-selectable flex-1">
                <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
            </div>
        </div>
    );
};

export default OptionCard;